import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { HttpService } from '../../http.service';
import FuzzySearch from 'fuzzy-search';

export interface LoadTeamsResult {
  teams: any[];
  teamsMeta: any;
  allTeamIdsTtl: any[];
  unassignedTeam: string;
  team_members: any[];
  team_name: string;
  team_id: string;
  teamMemberArr: any[];
}

@Injectable({ providedIn: 'root' })
export class TeamManagementService {

  constructor(private httpService: HttpService) {}

  // ── Pure transformations ──────────────────────────────────────────────────

  buildHierarchicalData(teams: any[]): any[][] {
    const hirarchicalgridData: any[][] = [];
    if (!teams?.length) return hirarchicalgridData;
    const firstChar = teams[0]?.['name']?.[0];
    let counter = 0;
    const tempTeamArrNotIncludedNum: any[] = [];
    if (parseInt(firstChar)) {
      teams.forEach((team) => {
        if (parseInt(team.name)) {
          if (hirarchicalgridData[team.name[0] - 1]) {
            hirarchicalgridData[team.name[0] - 1].push(team);
          } else {
            hirarchicalgridData[team.name[0] - 1] = [team];
          }
        } else {
          tempTeamArrNotIncludedNum.push(team);
        }
      });
      hirarchicalgridData.push(tempTeamArrNotIncludedNum);
    } else {
      hirarchicalgridData[counter] = [];
      teams.forEach((team) => hirarchicalgridData[counter].push(team));
    }
    return hirarchicalgridData;
  }

  fuzzySearchTeams(teams: any[], term: string): any[] {
    let resultMembers = [...teams];
    let resultArr: any[] = [];
    const searcher = new FuzzySearch(resultMembers, ['name'], { caseSensitive: false });
    resultArr = searcher.search(term);
    return resultArr;
  }

  sortTeamMembers(memberships: any[]): any[] {
    return [...memberships].sort((a, b) => {
      const getName = (m: any) => {
        if (m?.last_name) return m.last_name.toLowerCase();
        if (m?.default_identity?.last_name) return m.default_identity.last_name.toLowerCase();
        if (m?.default_profile?.last_name) return m.default_profile.last_name.toLowerCase();
        return 'no name';
      };
      const an = getName(a), bn = getName(b);
      return an < bn ? -1 : an > bn ? 1 : 0;
    });
  }

  buildAllTeamIdsTtl(teams: any[], defaultShowingMembers: number): any[] {
    return teams.map(team => ({
      teamId: team.id,
      showMember: defaultShowingMembers,
      teamName: team.name,
      memberships_count: team.memberships_count,
    }));
  }

  // ── API: Load teams ───────────────────────────────────────────────────────

  loadTeams(orgId: string, params: { queryType: string; sortType: string; perPage: number; page: number }, defaultShowingMembers: number): Observable<LoadTeamsResult> {
    const url = `${environment.profileURL}/api/v1/organizations/${orgId}/teams.json` +
      `?${params.queryType}=${params.sortType}&per_page=${params.perPage}&page=${params.page}`;
    return (this.httpService.getRequest(url) as Observable<any>).pipe(
      map(data => {
        const allTeams: any[] = data['teams'] || [];
        const filtered = allTeams.filter((t: any) =>
          t.name.toLowerCase() !== 'admin' && t.name.toLowerCase() !== 'unassigned'
        );
        const removedCount = allTeams.length - filtered.length;
        data['teams'] = filtered;
        const teams: any[] = filtered.map((team: any) => ({
          ...team,
          memberships: this.sortTeamMembers(team.memberships || []),
        }));
        const unassignedTeam = data['teams'].find((t: any) => t.name === 'Unassigned')?.id || '';
        const meta = data['meta'] ? { ...data['meta'], total_count: (data['meta'].total_count || 0) - removedCount } : data['meta'];
        return {
          teams,
          teamsMeta: meta,
          allTeamIdsTtl: this.buildAllTeamIdsTtl(teams, defaultShowingMembers),
          unassignedTeam,
          team_members: teams[0]?.memberships || [],
          team_name: teams[0]?.name || '',
          team_id: teams[0]?.id || '',
          teamMemberArr: filtered.map((e: any) => ({ id: e.id, memberships_count: e?.memberships_count })),
        };
      })
    );
  }

  searchTeams(orgId: string, params: { queryType: string; sortType: string; teamSrchBy: string; teamSrchName: string; perPage: number; page: number }): Observable<any[]> {
    const url = `${environment.profileURL}/api/v1/organizations/${orgId}/teams.json` +
      `?${params.queryType}=${params.sortType}&${params.teamSrchBy}=${params.teamSrchName}&per_page=${params.perPage}&page=${params.page}`;
    return (this.httpService.getRequest(url) as Observable<any>).pipe(
      map(data => this.fuzzySearchTeams(data?.teams || [], params.teamSrchName))
    );
  }

  // ── API: Team CRUD ────────────────────────────────────────────────────────

  addTeam(orgId: string, teamName: string): Observable<any> {
    return from(this.httpService.postRequest(
      `${environment.profileURL}/api/v1/organizations/${orgId}/teams.json`,
      { name: teamName, organization_id: orgId }
    ) as Promise<Observable<any>>).pipe(switchMap(obs => obs));
  }

  updateTeam(orgId: string, teamId: string, name: string): Observable<any> {
    return from(this.httpService.putRequest(
      `${environment.profileURL}/api/v1/organizations/${orgId}/teams/${teamId}.json`,
      { name }
    ) as Promise<Observable<any>>).pipe(switchMap(obs => obs));
  }

  deleteTeam(orgId: string, teamId: string): Observable<any> {
    return from(this.httpService.deleteRequest(
      `${environment.profileURL}/api/v1/organizations/${orgId}/teams/${teamId}.json`
    ) as Promise<Observable<any>>).pipe(switchMap(obs => obs));
  }
}
